// components
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { HorizontalCard } from '@/pwa/atoms/HorizontalCard/HorizontalCard';
import { BenefitCardContent } from '@/pwa/atoms/BenefitCardContent/BenefitCardContent';
import { Icons } from '@/components/atoms/Icon/icons';

// mui
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export interface BenefitItem {
  benefitName: string;
  benefitDescription: string;
  icon: Icons;
  savingsMessage: string;
  eligibilityInformation: string[];
  additionalBenefits: string[];
  applicationInstructions: string;
  annualValueGBP?: {
    min?: number;
    max?: number;
    notes?: string;
  };
  links?: {
    info?: string;
    apply?: string;
  };
  sources: string[];
}

interface BenefitCardsListProps {
  benfitItems: BenefitItem[];
  onContinueAssessment?: () => void;
}

/**
 * BenefitCardsList renders a list of benefit cards.
 */
const BenefitCardsList = ({
  benfitItems,
  onContinueAssessment,
}: BenefitCardsListProps) => (
  <Stack spacing={2}>
    {benfitItems.length > 0 ? (
      benfitItems.map((item, index) => {
        let subtitle = item.annualValueGBP?.notes;
        if (
          item.annualValueGBP?.max !== undefined &&
          item.annualValueGBP?.min !== undefined
        ) {
          subtitle = `\u00a3${item?.annualValueGBP?.min?.toLocaleString()}-\u00a3${item?.annualValueGBP?.max?.toLocaleString()}/year`;
        }
        return (
          <HorizontalCard
            key={index}
            title={item.benefitName}
            subtitle={subtitle}
            icon={item.icon}
            slotExpanded={
              <BenefitCardContent
                benefitName={item.benefitName}
                benefitDescription={item.benefitDescription}
                savingsMessage={item.savingsMessage}
                eligibilityInformation={item.eligibilityInformation}
                additionalBenefits={item.additionalBenefits}
                applicationInstructions={item.applicationInstructions}
                links={item.links}
                sources={item.sources}
                onContinueAssessment={onContinueAssessment}
              />
            }
          />
        );
      })
    ) : (
      <Typography variant='body2' align='center'>
        No benefits found
      </Typography>
    )}
  </Stack>
);

export interface EligibilityResultsProps {
  onBack: () => void;
  title?: string;
  eligibleBenefitItems?: BenefitItem[];
  ineligibleBenefitItems?: BenefitItem[];
  onContinueAssessment?: () => void;
  onPrevious?: () => void;
}

/**
 * EligibilityResults page shows the results of an eligibility check.
 */
export const EligibilityResults = ({
  onBack,
  title = 'Eligibility Calculator Results',
  eligibleBenefitItems = [],
  ineligibleBenefitItems = [],
  onContinueAssessment,
  onPrevious,
}: EligibilityResultsProps) => {
  const theme = useTheme();

  return (
    <ModalPageTemplate
      onBack={onBack}
      footerSlot={
        <Box
          sx={{
            p: 2,
            bgcolor: 'background.paper',
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack
            spacing={2}
            sx={{
              maxWidth: 'sm',
              mx: 'auto',
            }}
          >
            {onContinueAssessment && (
              <Button
                variant='contained'
                size='large'
                fullWidth
                onClick={onContinueAssessment}
              >
                Continue Assessment
              </Button>
            )}
            {onPrevious && (
              <Button
                variant='outlined'
                size='large'
                fullWidth
                onClick={onPrevious}
              >
                Previous
              </Button>
            )}
          </Stack>
        </Box>
      }
    >
      <PageTemplate
        maxWidth='sm'
        headerSlot={
          <PageHeader
            title={title}
            description='Take a moment to review the benefits you may be eligible for, then either get in touch with your Care Expert to discuss next steps, or get started straight away by completing the linked forms.'
          />
        }
        contentSlot={
          <Stack spacing={4}>
            <Alert severity='info'>
              Unsure about what to do with this information? Book a call with
              one of our Care Experts and they&apos;ll walk you through your
              next steps.
            </Alert>

            <Stack spacing={2}>
              <Typography variant='h2'>
                It looks like you&apos;re eligible for
              </Typography>
              <BenefitCardsList
                benfitItems={eligibleBenefitItems}
                onContinueAssessment={onContinueAssessment}
              />
            </Stack>
          </Stack>
        }
      />
    </ModalPageTemplate>
  );
};

EligibilityResults.displayName = 'EligibilityResults';
export default EligibilityResults;
